import { useApolloClient, useQuery, useSubscription } from '@apollo/client';
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { GET_NOTIFICATIONS, GET_UNREAD_COUNT } from '../../graphQLDefs/Notifications/queries';
import {
  NOTIFICATION_RECEIVED,
  NOTIFICATION_UPDATED,
  NOTIFICATION_DELETED,
} from '../../graphQLDefs/Notifications/subscriptions';
import {
  type GetNotificationsInput,
  type GetNotificationsResponse,
  type Notification,
  type NotificationFilter,
  EstimateStatus,
  JobStatus,
} from '../../generated/graphql';
import { GET_MESSAGES } from '../../graphql/chats';
import { GET_ESTIMATE_DETAILS } from '../../graphql/estimates';
import { logger } from '../../utils/logger';

// Mark as read functionality
import { useMarkNotificationsAsRead } from './useMarkNotificationsAsRead';

interface UseNotificationsOptions {
  skip?: boolean;
  initialLimit?: number;
  filter?: NotificationFilter | undefined;
}

export const useNotifications = (options: UseNotificationsOptions = {}) => {
  const { skip = false, initialLimit = 20, filter } = options;
  const apolloClient = useApolloClient();

  // State for infinite scroll
  const [allNotifications, setAllNotifications] = useState<Notification[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pendingReadIds, setPendingReadIds] = useState<Set<string>>(() => new Set());
  const optimisticReadAt = useRef(new Map<string, string>());
  const confirmedReadIds = useRef(new Set<string>());

  const variables: GetNotificationsInput = {
    limitPerPage: initialLimit,
    page: currentPage,
    ...(filter && { filter }),
  };

  const { data, loading, error, refetch } = useQuery<{
    getNotifications: GetNotificationsResponse;
  }>(GET_NOTIFICATIONS, {
    variables: { data: variables },
    skip,
    fetchPolicy: 'network-only',
    errorPolicy: 'all', // Include partial results with errors
    onError: error => {
      logger.error({
        message: error.message,
      });
    },
  });

  // Get unread count
  const { data: unreadCountData, refetch: rawRefetchUnreadCount } = useQuery(GET_UNREAD_COUNT, {
    skip,
    fetchPolicy: 'network-only',
  });

  const refetchUnreadCount = useCallback(async () => {
    const confirmedAtRequest = new Set(confirmedReadIds.current);
    const result = await rawRefetchUnreadCount();
    if (result?.data?.getUnreadCount?.__typename === 'GetUnreadCount' && confirmedAtRequest.size) {
      confirmedAtRequest.forEach(id => confirmedReadIds.current.delete(id));
      setPendingReadIds(prev => {
        const next = new Set(prev);
        confirmedAtRequest.forEach(id => next.delete(id));
        return next;
      });
    }
    return result;
  }, [rawRefetchUnreadCount]);

  const applyOptimisticRead = useCallback((notification: Notification): Notification => {
    const readAt = optimisticReadAt.current.get(notification.id);
    if (notification.readAt) {
      optimisticReadAt.current.delete(notification.id);
      return notification;
    }
    return readAt ? { ...notification, readAt } : notification;
  }, []);

  // Subscribe to new notifications
  useSubscription(NOTIFICATION_RECEIVED, {
    skip,
    onData: ({ data: subscriptionData }) => {
      const event = subscriptionData.data?.notificationReceived;
      if (event?.__typename === 'NotificationReceived') {
        const newNotification = event.notification;
        setAllNotifications(prev => [newNotification as Notification, ...prev]);
        // Also refetch unread count
        refetchUnreadCount();

        const typeStr = (newNotification.type || '').toUpperCase();
        const titleStr = (newNotification.title || '').toLowerCase();
        let targetEstimateStatus: EstimateStatus | null = null;
        if (typeStr === 'ESTIMATE_REJECTED' || titleStr.includes('rejected')) {
          targetEstimateStatus = EstimateStatus.Rejected;
        } else if (typeStr === 'ESTIMATE_APPROVED' || titleStr.includes('approved')) {
          targetEstimateStatus = EstimateStatus.Approved;
        } else if (
          typeStr === 'ESTIMATE_CHANGES_REQUESTED' ||
          titleStr.includes('changes requested')
        ) {
          targetEstimateStatus = EstimateStatus.ChangesRequested;
        } else if (typeStr === 'ESTIMATE_EXPIRED' || titleStr.includes('expired')) {
          targetEstimateStatus = EstimateStatus.Expired;
        } else if (typeStr === 'ESTIMATE_SENT' || titleStr.includes('ready for review')) {
          targetEstimateStatus = EstimateStatus.ReadyForReview;
        }

        let meta: Record<string, unknown> | null = null;
        if (newNotification.metadata) {
          if (typeof newNotification.metadata === 'string') {
            try {
              meta = JSON.parse(newNotification.metadata) as Record<string, unknown>;
            } catch {
              meta = {};
            }
          } else if (typeof newNotification.metadata === 'object') {
            meta = newNotification.metadata as Record<string, unknown>;
          }
        }
        let dObj: Record<string, unknown> | null = null;
        if (newNotification.data) {
          if (typeof newNotification.data === 'string') {
            try {
              dObj = JSON.parse(newNotification.data) as Record<string, unknown>;
            } catch {
              dObj = {};
            }
          } else if (typeof newNotification.data === 'object') {
            dObj = newNotification.data as Record<string, unknown>;
          }
        }

        let estId = (meta?.estimateId ||
          meta?.estimate_id ||
          dObj?.estimateId ||
          dObj?.estimate_id) as string | undefined;
        if (!estId && newNotification.actionUrl) {
          const match = newNotification.actionUrl.match(/\/estimates\/([a-zA-Z0-9_-]+)/);
          if (match) estId = match[1];
        }
        if (estId && targetEstimateStatus && apolloClient?.cache) {
          const entityId = apolloClient.cache.identify({ __typename: 'Estimate', id: estId });
          if (entityId) {
            apolloClient.cache.modify({
              id: entityId,
              fields: {
                status() {
                  return targetEstimateStatus;
                },
              },
            });
          }
        }

        if (newNotification.type === 'ESTIMATE_REJECTED') {
          window.setTimeout(() => {
            void apolloClient?.refetchQueries?.({
              include: [GET_ESTIMATE_DETAILS, GET_MESSAGES],
            });
          }, 2000);
        }
        let targetStatus: JobStatus | null = null;
        if (typeStr === 'JOB_COMPLETED' || titleStr.includes('completed')) {
          targetStatus = JobStatus.Completed;
        } else if (typeStr === 'JOB_CONFIRMED' || titleStr.includes('confirmed')) {
          targetStatus = JobStatus.Confirmed;
        } else if (
          typeStr === 'JOB_CANCELLED' ||
          typeStr === 'JOB_CANCELED' ||
          titleStr.includes('canceled') ||
          titleStr.includes('cancelled')
        ) {
          targetStatus = JobStatus.Canceled;
        } else if (typeStr === 'JOB_STARTED' || titleStr.includes('started')) {
          targetStatus = JobStatus.Started;
        }

        let jId = (meta?.jobId || meta?.job_id || dObj?.jobId || dObj?.job_id) as
          | string
          | undefined;
        if (!jId && newNotification.actionUrl) {
          const match = newNotification.actionUrl.match(/\/jobs\/([a-zA-Z0-9_-]+)/);
          if (match) jId = match[1];
        }
        if (jId && targetStatus && apolloClient?.cache) {
          const entityId = apolloClient.cache.identify({ __typename: 'Job', id: jId });
          if (entityId) {
            apolloClient.cache.modify({
              id: entityId,
              fields: {
                status() {
                  return targetStatus;
                },
              },
            });
          }
        }

        void apolloClient?.refetchQueries?.({
          include: [
            'GetNotifications',
            'GetUnreadCount',
            'GetJobs',
            'GetJobsListPage',
            'getJob',
            'GetJob',
          ],
        });
        logger.info('[Subscription] New notification received', { id: newNotification.id });
      } else if (event?.__typename === 'Error') {
        logger.error({
          message: '[Subscription] NOTIFICATION_RECEIVED error',
          context: { error: event.message, errorCode: event.errorCode },
        });
      }
    },
    onError: error => {
      logger.error({
        message: '[Subscription] NOTIFICATION_RECEIVED connection error',
        context: { error: error.message },
      });
    },
  });

  // Subscribe to notification updates
  useSubscription(NOTIFICATION_UPDATED, {
    skip,
    onData: ({ data: subscriptionData }) => {
      const event = subscriptionData.data?.notificationUpdated;
      if (event?.__typename === 'NotificationUpdated') {
        const updatedNotification = applyOptimisticRead(event.notification as Notification);
        setAllNotifications(prev =>
          prev.map(notification =>
            notification.id === updatedNotification.id
              ? { ...notification, ...updatedNotification }
              : notification
          )
        );
        logger.info('[Subscription] Notification updated', { id: updatedNotification.id });
      } else if (event?.__typename === 'Error') {
        logger.error({
          message: '[Subscription] NOTIFICATION_UPDATED error',
          context: { error: event.message, errorCode: event.errorCode },
        });
      }
    },
    onError: error => {
      logger.error({
        message: '[Subscription] NOTIFICATION_UPDATED connection error',
        context: { error: error.message },
      });
    },
  });

  // Subscribe to notification deletions
  useSubscription(NOTIFICATION_DELETED, {
    skip,
    onData: ({ data: subscriptionData }) => {
      const event = subscriptionData.data?.notificationDeleted;
      if (event?.__typename === 'NotificationDeleted') {
        const deletedNotificationId = event.notificationId;
        setAllNotifications(prev =>
          prev.filter(notification => notification.id !== deletedNotificationId)
        );
        // Also refetch unread count
        refetchUnreadCount();
        logger.info('[Subscription] Notification deleted', { id: deletedNotificationId });
      } else if (event?.__typename === 'Error') {
        logger.error({
          message: '[Subscription] NOTIFICATION_DELETED error',
          context: { error: event.message, errorCode: event.errorCode },
        });
      }
    },
    onError: error => {
      logger.error({
        message: '[Subscription] NOTIFICATION_DELETED connection error',
        context: { error: error.message },
      });
    },
  });

  // Handle data updates with useEffect to avoid closure issues
  useEffect(() => {
    if (data?.getNotifications?.__typename === 'GetNotifications') {
      const notificationsData = data.getNotifications;
      const newNotifications = (notificationsData.notifications || []).map(applyOptimisticRead);
      const paginationInfo = notificationsData.paginationInfo;

      if (currentPage === 1) {
        // First page - replace all notifications
        setAllNotifications(newNotifications);
      } else {
        // Subsequent pages - append new notifications
        setAllNotifications(prev => [...prev, ...newNotifications]);
      }

      // Update pagination state
      if (paginationInfo && paginationInfo.currentPage && paginationInfo.totalPages) {
        setHasMore(paginationInfo.currentPage < paginationInfo.totalPages);
      } else {
        // If no pagination info, assume we have all data
        setHasMore(false);
      }

      setIsLoadingMore(false);
    } else if (data?.getNotifications?.__typename === 'Error') {
      setIsLoadingMore(false);
    }
  }, [data, currentPage, applyOptimisticRead]);

  // Check if the response contains an error
  const graphQLError =
    data?.getNotifications?.__typename === 'Error'
      ? {
          message: data.getNotifications.message,
          errorCode: data.getNotifications.errorCode,
        }
      : null;

  // Load more notifications for infinite scroll
  const loadMore = useCallback(() => {
    if (!loading && !isLoadingMore && hasMore) {
      setIsLoadingMore(true);
      setCurrentPage(prev => prev + 1);
    }
  }, [loading, isLoadingMore, hasMore]);

  // Reset pagination (useful for refresh)
  const resetPagination = useCallback(() => {
    setAllNotifications([]);
    setCurrentPage(1);
    setHasMore(true);
    setIsLoadingMore(false);
  }, []);

  // Enhanced refetch that resets pagination
  const refetchNotifications = useCallback(async () => {
    setIsRefreshing(true);
    resetPagination();

    try {
      // Wait a tick for state to update, then refetch
      const result = await refetch({
        data: {
          limitPerPage: initialLimit,
          page: 1,
          ...(filter && { filter }),
        },
      });
      // Manually process the refetch result since useEffect might not trigger
      if (result?.data?.getNotifications?.__typename === 'GetNotifications') {
        const notificationsData = result.data.getNotifications;
        const newNotifications = (notificationsData.notifications || []).map(applyOptimisticRead);
        setAllNotifications(newNotifications);

        // Update pagination state
        if (
          notificationsData.paginationInfo &&
          notificationsData.paginationInfo.currentPage &&
          notificationsData.paginationInfo.totalPages
        ) {
          setHasMore(
            notificationsData.paginationInfo.currentPage <
              notificationsData.paginationInfo.totalPages
          );
        } else {
          setHasMore(false);
        }
      }

      return result;
    } finally {
      setIsRefreshing(false);
    }
  }, [refetch, resetPagination, initialLimit, filter, applyOptimisticRead]);

  // Mark as read functionality
  const { markAsRead: markNotificationsAsRead } = useMarkNotificationsAsRead({
    onSuccess: updatedNotificationIds => {
      // Update local state when notifications are marked as read
      setAllNotifications(prev =>
        prev.map(notification => {
          if (updatedNotificationIds.includes(notification.id)) {
            return { ...notification, readAt: new Date().toISOString() };
          }
          return notification;
        })
      );
    },
  });

  // Wrapper function to maintain the same interface
  const markAsRead = useCallback(
    async (notificationId: string) => {
      const wasUnread = allNotifications.some(
        notification => notification.id === notificationId && !notification.readAt
      );
      if (wasUnread) {
        const readAt = new Date().toISOString();
        optimisticReadAt.current.set(notificationId, readAt);
        setAllNotifications(prev =>
          prev.map(notification =>
            notification.id === notificationId ? { ...notification, readAt } : notification
          )
        );
        setPendingReadIds(prev => new Set(prev).add(notificationId));
      }

      let result: Awaited<ReturnType<typeof markNotificationsAsRead>>;
      try {
        result = await markNotificationsAsRead([notificationId]);
      } catch (error) {
        if (wasUnread) {
          optimisticReadAt.current.delete(notificationId);
          confirmedReadIds.current.delete(notificationId);
          setAllNotifications(prev =>
            prev.map(notification =>
              notification.id === notificationId ? { ...notification, readAt: null } : notification
            )
          );
        }
        if (wasUnread) {
          setPendingReadIds(prev => {
            const next = new Set(prev);
            next.delete(notificationId);
            return next;
          });
        }
        throw error;
      }

      if (wasUnread) confirmedReadIds.current.add(notificationId);
      await refetchUnreadCount();
      return result;
    },
    [allNotifications, markNotificationsAsRead, refetchUnreadCount]
  );

  // Memoize unread count from the dedicated query
  const unreadCount = useMemo(() => {
    if (unreadCountData?.getUnreadCount?.__typename === 'GetUnreadCount') {
      return Math.max(0, (unreadCountData.getUnreadCount.count || 0) - pendingReadIds.size);
    }
    // Fallback to local calculation
    return allNotifications.filter(notification => !notification.readAt).length;
  }, [unreadCountData, allNotifications, pendingReadIds]);

  const isInitialLoading = loading && currentPage === 1 && !isRefreshing;
  const isShowingLoading = isInitialLoading || isRefreshing;

  return {
    notifications: allNotifications,
    paginationInfo:
      data?.getNotifications?.__typename === 'GetNotifications'
        ? data.getNotifications.paginationInfo
        : null,
    unreadCount,
    loading: isShowingLoading,
    loadingMore: isLoadingMore,
    error: graphQLError || error,
    hasMore,
    loadMore,
    refetch: refetchNotifications,
    resetPagination,
    // Mark as read functionality
    markAsRead,
    // Expose refetch for unread count
    refetchUnreadCount,
  };
};
